import React, { useCallback, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Upload, FileText, Trash2, Loader2, Sparkles, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { sentimentLabels } from '../lib/data';
import { getAnalyses, addAnalysis, predict, sentimentColorClass, confidenceLabel, confidenceBadgeClass } from '../lib/predictive';
interface Attachment {
  id: string;
  name: string;
  size: string;
}
const EXAMPLE_ATTACHMENTS: {
  name: string;
  size: string;
}[] = [{
  name: 'Comunicato — Giornata della Memoria 2026.pdf',
  size: '232 KB'
}, {
  name: 'Comunicato — Inaugurazione nuova mostra MEIS.docx',
  size: '88 KB'
}];
function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}
export function PredictiveAnalysis() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  // Bump to re-read the store after a new analysis is added
  const [version, setVersion] = useState(0);
  const analyses = getAnalyses();
  const addAttachments = useCallback((names: {
    name: string;
    size: string;
  }[]) => {
    setAttachments(prev => [...prev, ...names.map(n => ({
      id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: n.name,
      size: n.size
    }))]);
  }, []);
  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    addAttachments(Array.from(files).map(f => ({
      name: f.name,
      size: `${Math.max(1, Math.round(f.size / 1024))} KB`
    })));
  };
  const removeAttachment = (id: string) => setAttachments(prev => prev.filter(a => a.id !== id));
  const runAnalysis = () => {
    if (attachments.length === 0) return;
    setAnalyzing(true);
    setTimeout(() => {
      const created = attachments.map(a => predict(a.name, a.size));
      created.forEach(c => addAnalysis(c));
      setAttachments([]);
      setAnalyzing(false);
      setVersion(v => v + 1);
      // Vai subito al dettaglio della prima previsione generata
      if (created.length > 0) navigate(`/predictive/${created[0].id}`);
    }, 1400);
  };
  return <div className="px-6 py-6 max-w-[1400px] mx-auto w-full space-y-8" data-id="element-1043">
      {/* Header */}
      <header data-id="element-1044">
        <div className="flex items-center gap-2 text-sm text-muted-foreground font-heading mb-2" data-id="element-1045">
          <TrendingUp className="h-4 w-4" data-id="element-1046" />
          Analisi predittiva
        </div>
        <h1 className="text-2xl md:text-3xl font-normal tracking-tight" data-id="element-1047">
          Impatto mediatico previsto
        </h1>
        <p className="text-sm text-muted-foreground leading-relaxed mt-3 max-w-2xl" data-id="element-1048">
          Allega un comunicato stampa: il sistema stima il potenziale impatto
          sui media confrontandolo con lo storico delle rassegne (score di
          rilevanza, AVE, sentiment, tier e testate).
        </p>
      </header>

      {/* Attachment upload */}
      <section className="bg-card border border-border rounded-lg p-6 space-y-4" data-id="element-1049">
        <h2 className="text-lg font-normal flex items-center gap-2" data-id="element-1050">
          <Upload className="h-4 w-4 text-accent" data-id="element-1051" />
          Allega comunicato
        </h2>

        <div role="button" tabIndex={0} aria-label="Carica un comunicato stampa" onClick={() => fileInputRef.current?.click()} onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          fileInputRef.current?.click();
        }
      }} onDragOver={e => {
        e.preventDefault();
        setDragOver(true);
      }} onDragLeave={() => setDragOver(false)} onDrop={e => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
      }} className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-10 text-center cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 ${dragOver ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/60 hover:bg-muted/30'}`} data-id="element-1052">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center" data-id="element-1053">
            <Upload className="h-5 w-5 text-muted-foreground" data-id="element-1054" />
          </div>
          <p className="text-sm font-medium text-foreground" data-id="element-1055">
            Trascina il comunicato qui
          </p>
          <p className="text-xs text-muted-foreground" data-id="element-1056">
            oppure clicca per selezionare · PDF, DOC, DOCX
          </p>
          <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" multiple className="hidden" onChange={e => {
          handleFiles(e.target.files);
          e.target.value = '';
        }} data-id="element-1057" />
        </div>

        {/* Example attachments helper */}
        {attachments.length === 0 && <div className="flex flex-wrap items-center gap-2" data-id="element-1058">
            <span className="text-xs text-muted-foreground" data-id="element-1059">Esempi:</span>
            {EXAMPLE_ATTACHMENTS.map(ex => <button key={ex.name} onClick={() => addAttachments([ex])} className="text-xs px-2.5 py-1 rounded-full border border-border text-accent hover:bg-accent/10 transition-colors" data-id="element-1060">
                + {ex.name.replace('Comunicato — ', '')}
              </button>)}
          </div>}

        {/* Attached files list */}
        {attachments.length > 0 && <ul className="space-y-2" data-id="element-1061">
            {attachments.map(a => <li key={a.id} className="flex items-center gap-3 rounded-md border border-border bg-muted/20 px-3 py-2.5" data-id="element-1062">
                <FileText className="h-4 w-4 text-accent flex-shrink-0" data-id="element-1063" />
                <span className="text-sm text-foreground truncate flex-1" data-id="element-1064">
                  {a.name}
                </span>
                <span className="text-xs text-muted-foreground flex-shrink-0" data-id="element-1065">
                  {a.size}
                </span>
                <button onClick={() => removeAttachment(a.id)} aria-label={`Rimuovi ${a.name}`} className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0" data-id="element-1066">
                  <Trash2 className="h-4 w-4" data-id="element-1067" />
                </button>
              </li>)}
          </ul>}

        <div className="flex justify-end" data-id="element-1068">
          <Button onClick={runAnalysis} disabled={attachments.length === 0 || analyzing} className="rounded-full h-11 px-6 text-sm" data-id="element-1069">
            {analyzing ? <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" data-id="element-1070" />
                Analisi in corso…
              </> : <>
                <Sparkles className="h-4 w-4 mr-2" data-id="element-1071" />
                Genera previsione
              </>}
          </Button>
        </div>
      </section>

      {/* Executed analyses table */}
      <section className="bg-card border border-border rounded-lg overflow-hidden" data-id="element-1072">
        <div className="px-6 pt-6 pb-4 border-b border-border" data-id="element-1073">
          <h2 className="text-lg font-normal" data-id="element-1074">Analisi eseguite</h2>
          <p className="text-xs text-muted-foreground mt-1" data-id="element-1075">
            Seleziona una riga per aprire l'analisi predittiva completa
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-1076">
          <Table data-id="element-1077">
            <TableHeader data-id="element-1078">
              <TableRow data-id="element-1079">
                <TableHead data-id="element-1080">Comunicato</TableHead>
                <TableHead data-id="element-1081">Data</TableHead>
                <TableHead className="text-right" data-id="element-1082">Score</TableHead>
                <TableHead className="text-right" data-id="element-1083">AVE stimato</TableHead>
                <TableHead className="text-right" data-id="element-1084">Articoli</TableHead>
                <TableHead data-id="element-1085">Sentiment</TableHead>
                <TableHead data-id="element-1086">Confidenza</TableHead>
                <TableHead className="w-10" data-id="element-1087" />
              </TableRow>
            </TableHeader>
            <TableBody data-id="element-1088">
              {analyses.map(a => <TableRow key={a.id} onClick={() => navigate(`/predictive/${a.id}`)} tabIndex={0} onKeyDown={e => {
              if (e.key === 'Enter') navigate(`/predictive/${a.id}`);
            }} className="cursor-pointer hover:bg-muted/30 transition-colors" data-id="element-1089">
                  <TableCell data-id="element-1090">
                    <div className="flex items-center gap-2.5 min-w-0" data-id="element-1091">
                      <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" data-id="element-1092" />
                      <span className="text-sm font-medium text-foreground truncate max-w-[280px]" data-id="element-1093">
                        {a.fileName}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap" data-id="element-1094">
                    {formatDate(a.createdAt)}
                  </TableCell>
                  <TableCell className="text-right text-accent font-medium" style={{
                fontVariantNumeric: 'tabular-nums'
              }} data-id="element-1095">
                    {a.predictedScore.toFixed(1)}
                  </TableCell>
                  <TableCell className="text-right text-sm whitespace-nowrap" style={{
                fontVariantNumeric: 'tabular-nums'
              }} data-id="element-1096">
                    €{(a.aveLow / 1000).toFixed(0)}k–€
                    {(a.aveHigh / 1000).toFixed(0)}k
                  </TableCell>
                  <TableCell className="text-right text-sm" style={{
                fontVariantNumeric: 'tabular-nums'
              }} data-id="element-1097">
                    ~{a.projectedArticles}
                  </TableCell>
                  <TableCell data-id="element-1098">
                    <span className="flex items-center gap-2 text-sm whitespace-nowrap" data-id="element-1099">
                      <span className={`w-2 h-2 rounded-full ${sentimentColorClass(a.expectedSentiment)}`} data-id="element-1100" />
                      {sentimentLabels[a.expectedSentiment]}
                    </span>
                  </TableCell>
                  <TableCell data-id="element-1101">
                    <Badge variant="secondary" className={`text-xs ${confidenceBadgeClass(a.confidence)}`} data-id="element-1102">
                      {confidenceLabel(a.confidence)}
                    </Badge>
                  </TableCell>
                  <TableCell data-id="element-1103">
                    <ChevronRight className="h-4 w-4 text-muted-foreground" data-id="element-1104" />
                  </TableCell>
                </TableRow>)}
            </TableBody>
          </Table>
        </div>
        {analyses.length === 0 && <div className="px-6 py-12 text-center text-sm text-muted-foreground" data-id="element-1105">
            Nessuna analisi eseguita. Allega un comunicato per iniziare.
          </div>}
      </section>
    </div>;
}